import { setScopedEntry } from "@frontend-shared/utils";
import { useCallback, useEffect, useRef, useState } from "react";

// NOTE: In-memory, per-session store of unsent chat input keyed by a scope key
// (see buildChatScopeKey). Retains a user's draft when they navigate between
// chats / chat history and back within the same session, without leaking one
// chat's draft into another (NSAI-3912). Cleared on full page reload by design —
// drafts are never persisted to storage. Entry count is capped (see
// setScopedEntry) so a long session can't grow it unbounded.
const chatDraftStore = new Map<string, string>();

// NOTE: Clear all retained drafts. Called on sign-out so one user's drafts can't
// hydrate the next session (logout is a client-side navigate, not a reload, so
// this module-level store would otherwise survive).
export const resetChatDraftStore = (): void => {
  chatDraftStore.clear();
};

interface IMigrateDraftProps {
  fromScopeKey: string;
  toScopeKey: string;
}

interface IUseChatDraftReturn {
  draft: string;
  setDraft: (value: string) => void;
  clearDraft: (scopeKey?: string) => void;
  migrateDraft: (props: IMigrateDraftProps) => void;
}

export const useChatDraft = (scopeKey: string): IUseChatDraftReturn => {
  // NOTE: Keep the active key in a ref so setDraft/clearDraft stay referentially
  // stable (like a useState setter). Callers pass them to memoized effects and
  // callbacks, so a changing identity would defeat that memoization.
  const draftKeyRef = useRef<string>(scopeKey);

  const [draftValue, setDraftValue] = useState<string>(
    () => chatDraftStore.get(scopeKey) ?? "",
  );

  // NOTE: When the active scope changes, point the ref at the new key and load
  // that scope's stored draft (empty if it has none). A single Chat component
  // instance is reused across routes, so this keyed sync — not a remount — is
  // what restores the right draft. setDraft/clearDraft only run from event
  // handlers, which fire after this effect has committed the current key.
  useEffect(() => {
    draftKeyRef.current = scopeKey;
    setDraftValue(chatDraftStore.get(scopeKey) ?? "");
  }, [scopeKey]);

  const setDraft = useCallback((value: string): void => {
    const key = draftKeyRef.current;
    if (value) {
      setScopedEntry({ store: chatDraftStore, key, value });
    } else {
      chatDraftStore.delete(key);
    }
    setDraftValue(value);
  }, []);

  // NOTE: Clears a specific scope's draft (defaults to the active scope).
  // Callers that clear after an awaited step (e.g. send-after-upload) pass the
  // scope key captured at call time so a mid-await chat switch can't delete the
  // wrong draft. The visible textarea is only blanked when the cleared scope is
  // still the active one — otherwise the user has navigated on and we must not
  // wipe the draft they are now looking at.
  const clearDraft = useCallback((scopeKeyToClear?: string): void => {
    const key = scopeKeyToClear ?? draftKeyRef.current;
    chatDraftStore.delete(key);
    if (key === draftKeyRef.current) {
      setDraftValue("");
    }
  }, []);

  // NOTE: Carries a draft to a new scope when a scope's identity changes under
  // the user — a brand-new chat receiving its server id mid-stream, where the
  // scope key flips from the __new_chat__ placeholder to the real chat id. The
  // typed value survives in the store under the old key, but the keyed sync
  // effect above reloads the new (empty) scope and would blank the textarea; the
  // follow-up a user began typing while the first reply streamed would vanish
  // (NSAI-3996). This moves the draft to the new key and, when that key is the
  // one now on screen, restores it. Never clobbers a draft already stored under
  // the destination.
  const migrateDraft = useCallback(
    ({ fromScopeKey, toScopeKey }: IMigrateDraftProps): void => {
      if (fromScopeKey === toScopeKey) return;
      const carried = chatDraftStore.get(fromScopeKey);
      if (!carried) return;
      chatDraftStore.delete(fromScopeKey);
      if (!chatDraftStore.has(toScopeKey)) {
        setScopedEntry({
          store: chatDraftStore,
          key: toScopeKey,
          value: carried,
        });
      }
      if (toScopeKey === draftKeyRef.current) {
        setDraftValue(chatDraftStore.get(toScopeKey) ?? "");
      }
    },
    [],
  );

  return { draft: draftValue, setDraft, clearDraft, migrateDraft };
};
